import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@repo/ui/card'
import { useT } from '@repo/ui/hooks'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { cn, genWidths } from '@repo/ui/utils'
import { RefreshCwIcon } from 'lucide-react'
import { ReactNode } from 'react'
import { IoSparklesSharp } from 'react-icons/io5'

import { BeneficiaryLink } from '@/components/beneficiary/BeneficiaryLink'
import { ItemUpdatedDate } from '@/components/CollapsibleCard'
import { defaultWidget } from '@/store/dashboard-widgets/slice'
import { DashboardWidget } from '@/store/dashboard-widgets/types'

export function Widget({
  widget,
  children,
  onForceRefresh,
}: {
  widget: DashboardWidget
  children: ReactNode
  onForceRefresh: () => void
}) {
  const { t, tc } = useT({ keyPrefix: 'dashboard.widgets' })
  const isDefault = widget.filterTemplate.id === defaultWidget.filterTemplate.id
  return (
    <Card className="flex flex-col bg-opacity-20">
      <CardHeader>
        <CardTitle className="text-primary flex items-center gap-1 text-base font-medium xl:text-base">
          <IoSparklesSharp className="text-primary-400 size-5" />{' '}
          {isDefault ? tc('defaultWidgetName') : widget.filterTemplate.name}{' '}
        </CardTitle>
        <CardDescription>
          <span className="text-primary-400 text-sm font-normal">
            {t('total', { count: widget.totalItems })}
          </span>
        </CardDescription>
        <CardAction>{children}</CardAction>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-2 pt-4 xl:pt-4">
        {widget.loading ? (
          <div className="mt-2 flex flex-col gap-2">
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </div>
        ) : (
          widget.shortList.map((b) => (
            <BeneficiaryLink
              key={b.id}
              id={b.id}
              fullName={b.fullName}
              className="text-base"
              withIcon={false}
            />
          ))
        )}
      </CardContent>
      <CardFooter className="flex-shrink-0 justify-between">
        <div className="flex items-center gap-1.5">
          {widget.lastUpdatedAt && (
            <ItemUpdatedDate
              labelClassName="hidden"
              className="text-xs"
              updatedAt={widget.lastUpdatedAt}
            />
          )}
          <TooltipWrapper content={tc('refresh', { cfl: true })} disabled={widget.loading}>
            <div onClick={onForceRefresh} className={cn(!widget.loading && 'cursor-pointer')}>
              <RefreshCwIcon
                className={cn(
                  'text-primary-500 hover:text-primary size-3',
                  widget.loading && 'animate-spin',
                )}
              />
            </div>
          </TooltipWrapper>
        </div>
      </CardFooter>
    </Card>
  )
}

function Skeleton({ className }: { className?: string }) {
  const random = genWidths(1, 15, 45)[0]
  const width = random + '%'
  return (
    <div className={cn('bg-primary-50 h-4 animate-pulse rounded', className)} style={{ width }} />
  )
}
